import { Image } from 'expo-image';
import { router } from 'expo-router';
import { useLocalSearchParams } from 'expo-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCallback } from 'react';
import {
  AccessibilityInfo,
  ImageStyle,
  Platform,
  StyleProp,
  View,
  findNodeHandle,
  useWindowDimensions,
} from 'react-native';

import { useToast } from '@fhs/auth/src/hooks';
import {
  ActionSheet,
  Badge,
  Button,
  ErrorCard,
  IconClose,
  IconTrophyOutline,
  Text,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';
import {
  ILoyaltyQuestStepUi,
  LoyaltyQuestImplementationType,
  LoyaltyQuestStatus,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';

import { ChallengeDetailsSkeleton } from '../../components';
import { useLoyaltyLegacyStates } from '../../hooks/use-loyalty-legacy-states';
import { useOnPressChallenge } from '../../hooks/use-on-press-challenge';
import { useChallenge } from '../../queries/loyalty.queries';
import { getChallengeBadge, getChallengeButtonText, getChallengeDescription } from '../../utils';

import { Milestone, MilestoneTracker } from './milestone-tracker';
import { StepProducts } from './step-products';

function ChallengeDetailScreenContent() {
  const styles = useMqStyles();
  const { show } = useToast();
  const { challengeId } = useLocalSearchParams<{
    challengeId: string;
  }>();
  const { loyaltyUserReady } = useLoyaltyLegacyStates();
  const {
    data: currentChallenge,
    isLoading: isLoyaltyIncentivesLoading,
    isError,
    refetch,
  } = useChallenge(challengeId);
  const onPressChallenge = useOnPressChallenge();
  const isLoading = isLoyaltyIncentivesLoading || !loyaltyUserReady;

  const [isOnPressChallengeLoading, setIsOnPressChallengeLoading] = useState(false);
  const headerFocusRef = useRef<View>(null);

  const isDesktop = useMqSelect({ $gteDesktop: true }, false);
  const { height: screenHeight } = useWindowDimensions();

  const onClose = useCallback(() => {
    router.dismiss();
  }, []);

  const onPressButton = useCallback(async () => {
    if (currentChallenge) {
      try {
        setIsOnPressChallengeLoading(true);
        await onPressChallenge(currentChallenge);
      } catch (error) {
        show({
          text: 'Something went wrong',
          variant: 'informative',
        });
      } finally {
        setIsOnPressChallengeLoading(false);
      }
    }
  }, [currentChallenge, onPressChallenge, show]);

  const containerStyle = isDesktop ? { height: screenHeight } : ({ height: '100%' } as const);

  const buttonText = currentChallenge?.status
    ? getChallengeButtonText(currentChallenge?.status)
    : undefined;

  const badge = currentChallenge?.status ? getChallengeBadge(currentChallenge?.status) : undefined;

  const completedMessage =
    currentChallenge?.status === LoyaltyQuestStatus.COMPLETED
      ? getChallengeDescription(currentChallenge)
      : null;

  const dateRangeLine = currentChallenge
    ? getChallengeDescription({
        ...currentChallenge,
        status: LoyaltyQuestStatus.ACTIVATED,
      })
    : '';

  useEffect(() => {
    if (isLoading || !currentChallenge) {
      return;
    }
    const t = setTimeout(() => {
      const node = headerFocusRef.current;
      if (!node) {
        return;
      }
      if (Platform.OS === 'web') {
        (node as unknown as { focus?: () => void }).focus?.();
        return;
      }
      const reactTag = findNodeHandle(node);
      if (reactTag != null) {
        AccessibilityInfo.setAccessibilityFocus(reactTag);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [isLoading, currentChallenge]);

  const milestones = useMemo(
    () =>
      currentChallenge?.stepDetails.map<Milestone>((step, index) => ({
        id: index.toString(),
        points: step.pointValue ?? 0,
        isCompleted: step.completed,
      })) ?? [],
    [currentChallenge?.stepDetails]
  );

  const mapProducts = (stepComponents: ILoyaltyQuestStepUi[]) =>
    stepComponents.map((stepComponent, index) => ({
      id: index.toString(),
      name: stepComponent.name,
      image: stepComponent.image,
    }));

  const isTierFrequencyChallenge =
    currentChallenge?.implementationType === LoyaltyQuestImplementationType.TIER_FREQUENCY;
  const isTrialProductChallenge =
    currentChallenge?.implementationType === LoyaltyQuestImplementationType.TRIAL_PRODUCT;

  const challengeBodyA11yLabel = useMemo(() => {
    if (!currentChallenge) {
      return '';
    }
    const chunks: string[] = [];
    if (badge) {
      chunks.push(badge);
    }
    if (currentChallenge.name) {
      chunks.push(currentChallenge.name);
    }
    if (dateRangeLine) {
      chunks.push(dateRangeLine);
    }
    chunks.push('How it works:');
    if (currentChallenge.description) {
      chunks.push(currentChallenge.description);
    }
    if (isTierFrequencyChallenge && milestones.length > 0) {
      const done = milestones.filter(m => m.isCompleted).length;
      chunks.push(`Milestone progress: ${done} of ${milestones.length} completed`);
    }
    if (isTrialProductChallenge) {
      const names = currentChallenge.stepDetails.flatMap(step =>
        step.stepComponents.map(c => c.name).filter(Boolean)
      );
      if (names.length > 0) {
        chunks.push(`Included items: ${names.join(', ')}`);
      }
    }
    if (completedMessage) {
      chunks.push(completedMessage);
    }
    if (currentChallenge.terms) {
      chunks.push(currentChallenge.terms);
    }
    return chunks.join('. ');
  }, [
    badge,
    completedMessage,
    currentChallenge,
    dateRangeLine,
    isTierFrequencyChallenge,
    isTrialProductChallenge,
    milestones,
  ]);

  return (
    <View style={containerStyle}>
      <View
        ref={headerFocusRef}
        accessibilityLabel={
          currentChallenge
            ? `Challenge Details, ${currentChallenge.name}`
            : 'Challenge Details'
        }
        accessibilityRole="header"
        accessible
        focusable
        style={styles.headerContainer}
        {...(Platform.OS === 'web'
          ? ({
              // Focus target on open so keyboard / SR start in the sheet, not the page behind.
              tabIndex: -1,
            } as Record<string, unknown>)
          : {})}
      >
        <Text.Ui accessible={false} size="md" weight="semibold" style={styles.headerTitle}>
          Challenge Details
        </Text.Ui>
        <Button
          accessibilityLabel="Close challenge details"
          type="ghost"
          style={styles.closeIcon}
          onPress={onClose}
        >
          <Button.Icon>
            <IconClose size={24} />
          </Button.Icon>
        </Button>
      </View>
      {isLoading ? (
        <ChallengeDetailsSkeleton />
      ) : isError || !currentChallenge ? (
        <ErrorCard buttonHandle={refetch} />
      ) : (
        <View style={styles.content}>
          <ActionSheet.ScrollView style={styles.scrollViewContainer} bounces={false}>
            <View style={styles.imageContainer}>
              <Image
                accessibilityLabel={`${currentChallenge.name} challenge image`}
                style={styles.image as StyleProp<ImageStyle>}
                source={{ uri: currentChallenge?.image }}
                contentFit="cover"
              />
            </View>
            <View style={styles.scrollContent}>
              <View
                accessibilityLabel={challengeBodyA11yLabel}
                accessibilityRole="text"
                accessible
              >
                <View accessible={false}>
                  {badge && (
                    <Badge
                      style={styles.badge}
                      icon={
                        <View
                          accessible={false}
                          accessibilityElementsHidden
                          importantForAccessibility="no-hide-descendants"
                        >
                          <IconTrophyOutline color={tokens.colors.$white} />
                        </View>
                      }
                    >
                      {badge}
                    </Badge>
                  )}
                  <Text.Heading type="two">{currentChallenge.name}</Text.Heading>
                  <Text.Ui size="sm" style={styles.description}>
                    {dateRangeLine}
                  </Text.Ui>
                  <Text.Ui size="md" weight="semibold" style={styles.howItWorks}>
                    How it works:
                  </Text.Ui>
                  <Text.Ui size="sm" style={styles.howItWorksDescription}>
                    {currentChallenge.description}
                  </Text.Ui>
                  {isTierFrequencyChallenge && (
                    <MilestoneTracker style={styles.steps} milestones={milestones} />
                  )}
                  {isTrialProductChallenge &&
                    currentChallenge.stepDetails.map(step => (
                      <StepProducts
                        style={[styles.steps, step.completed && styles.completedStepsProducts]}
                        products={mapProducts([...step.stepComponents])}
                      />
                    ))}
                  {completedMessage && (
                    <Badge style={styles.completedMessage}>{completedMessage}</Badge>
                  )}
                  {currentChallenge.terms && (
                    <>
                      <View style={styles.divider} />
                      <Text.Ui size="sm" style={styles.terms}>
                        {currentChallenge.terms}
                      </Text.Ui>
                    </>
                  )}
                </View>
              </View>
            </View>
          </ActionSheet.ScrollView>
          {buttonText && (
            <View style={styles.footer}>
              <Button
                accessibilityLabel={`${buttonText}, ${currentChallenge.name}`}
                style={styles.buttonContainer}
                size="xl"
                onPress={onPressButton}
                loading={isOnPressChallengeLoading}
              >
                <Button.Text>{buttonText}</Button.Text>
              </Button>
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export function ChallengeDetailScreen() {
  const styles = useMqStyles();
  const drawerProps = useMemo(() => ({ style: { flex: 1 } }), []);

  const onClose = useCallback(() => {
    router.dismiss();
  }, []);

  return (
    <ActionSheet
      isVisible
      onClose={onClose}
      containerStyle={styles.actionSheet}
      drawerProps={drawerProps}
      ignoreBottomSafeArea={Platform.OS === 'android'}
    >
      <ChallengeDetailScreenContent />
    </ActionSheet>
  );
}

const useMqStyles = createMqStyles({
  actionSheet: {
    $base: {
      height: '90%',
    },
  },
  headerContainer: {
    $base: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderBottomWidth: 1,
      borderColor: tokens.colors.$black10,
    },
    $gteDesktop: {
      paddingVertical: 28,
      paddingHorizontal: 28,
      justifyContent: 'flex-start',
    },
  },
  closeIcon: {
    $base: {
      position: 'absolute',
      right: 16,
    },
  },
  headerTitle: {
    $base: {
      fontSize: 16,
    },
    $gteDesktop: {
      fontSize: 24,
    },
  },
  content: {
    $base: {
      flex: 1,
      backgroundColor: tokens.colors.$white,
    },
    $gteDesktop: {
      width: 520,
    },
  },
  imageContainer: {
    $base: {
      borderBottomWidth: 1,
      borderBottomColor: tokens.colors.$blackOpacity04,
    },
  },
  image: {
    $base: {
      width: '100%',
      aspectRatio: 5 / 2,
    },
  },
  scrollViewContainer: {
    $base: {
      flex: 1,
    },
  },
  scrollContent: {
    $base: {
      paddingHorizontal: 16,
      paddingVertical: 20,
    },
    $gteDesktop: {
      paddingHorizontal: 28,
      paddingVertical: 28,
    },
  },
  badge: {
    $base: {
      alignSelf: 'baseline',
      marginBottom: 12,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 8,
      paddingHorizontal: 8,
    },
  },
  description: {
    $base: {
      paddingTop: 12,
    },
  },
  howItWorks: {
    $base: {
      paddingTop: 20,
    },
    $gteDesktop: {
      paddingTop: 28,
    },
  },
  howItWorksDescription: {
    $base: {
      paddingTop: 8,
    },
  },
  steps: {
    $base: {
      marginTop: 16,
    },
  },
  completedStepsProducts: {
    $base: {
      backgroundColor: tokens.colors.$successOpacity04,
    },
  },
  completedMessage: {
    $base: {
      marginTop: 20,
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderRadius: 8,
    },
  },
  divider: {
    $base: {
      height: 1,
      backgroundColor: tokens.colors.$blackOpacity10,
      marginTop: 20,
    },
    $gteDesktop: {
      marginTop: 28,
    },
  },
  terms: {
    $base: {
      paddingTop: 20,
    },
    $gteDesktop: {
      paddingTop: 28,
    },
  },
  footer: {
    $base: {
      borderTopWidth: 1,
      borderTopColor: tokens.colors.$blackOpacity10,
      alignItems: 'center',
      padding: 16,
    },
    $gteDesktop: {
      padding: 28,
    },
  },
  buttonContainer: {
    $base: {
      width: '100%',
    },
  },
});
